import { useTranslation } from 'react-i18next';
import { formatBookingDateShort, localeTagFromLang } from '../../utils/bookingLocaleDisplay';
import { stripEmojiUnicode } from '../../utils/stripEmoji';
import './BookingComponents.css';

interface Booking {
  service: string | {
    id: string;
    name: string;
    price: number | string;
    currency: string;
    duration?: string;
  };
  resource?: string | {
    id: string;
    name: string;
  };
  slot?: string;
  date?: string;
  formattedDate?: string;
  time?: string;
  price?: number | string;
  currency?: string;
  requiresPayment?: boolean;
  party_size?: number;
  preferred_zone?: string;
  remaining_capacity_estimate?: number;
}

interface BookingReviewProps {
  booking: Booking;
  onConfirm: () => void;
  onCancel: () => void;
  language?: string;
  /** Etiqueta opcional del botón principal (p.ej. "Añadir a la reserva" en modo carrito). */
  confirmLabel?: string;
}

export function BookingReview({ booking, onConfirm, onCancel, language, confirmLabel }: BookingReviewProps) {
  const { t, i18n } = useTranslation();
  const lng = language?.trim() || i18n.language || 'es';
  const tl = (key: string) => t(key, { lng });
  const localeTag = localeTagFromLang(lng);
  const serviceName = stripEmojiUnicode(
    typeof booking.service === 'string' ? booking.service : booking.service.name,
  );
  const resourceName = booking.resource
    ? stripEmojiUnicode(
        typeof booking.resource === 'string' ? booking.resource : booking.resource.name,
      )
    : null;
  const price = typeof booking.service === 'object' 
    ? booking.service.price 
    : booking.price;
  const currency = typeof booking.service === 'object'
    ? booking.service.currency
    : booking.currency || 'EUR';
  const isoDate = booking.date?.slice(0, 10);
  const formattedDate =
    isoDate && /^\d{4}-\d{2}-\d{2}$/.test(isoDate)
      ? formatBookingDateShort(isoDate, localeTag)
      : stripEmojiUnicode(String(booking.formattedDate || booking.date || ''));
  const time = booking.time || '';
  const partySize = typeof booking.party_size === 'number' ? booking.party_size : null;
  const preferredZone = booking.preferred_zone?.trim() || '';
  const remainingCapacity =
    typeof booking.remaining_capacity_estimate === 'number'
      ? booking.remaining_capacity_estimate
      : null;

  return (
    <div className="booking-step booking-step--flow-left">
      <h2 className="booking-step-heading">{tl('booking.summary_title')}</h2>

      <div className="booking-summary">
        <div className="summary-item">
          <span className="summary-label">{tl('booking.service')}</span>
          <span className="summary-value">{serviceName}</span>
        </div>

        {formattedDate && (
          <div className="summary-item">
            <span className="summary-label">{tl('booking.date')}</span>
            <span className="summary-value">{formattedDate}</span>
          </div>
        )}

        {time && (
          <div className="summary-item">
            <span className="summary-label">{tl('booking.time')}</span>
            <span className="summary-value">{time}</span>
          </div>
        )}

        {resourceName && (
          <div className="summary-item">
            <span className="summary-label">{tl('booking.professional')}</span>
            <span className="summary-value">{resourceName}</span>
          </div>
        )}

        {partySize && (
          <div className="summary-item">
            <span className="summary-label">Personas</span>
            <span className="summary-value">{partySize}</span>
          </div>
        )}

        {preferredZone && (
          <div className="summary-item">
            <span className="summary-label">Zona</span>
            <span className="summary-value">{preferredZone}</span>
          </div>
        )}

        {remainingCapacity !== null && (
          <div className="summary-item">
            <span className="summary-label">Capacidad restante</span>
            <span className="summary-value">{remainingCapacity}</span>
          </div>
        )}

        {price !== undefined && price !== null && (
          <div className="summary-item">
            <span className="summary-label">{tl('booking.price')}</span>
            <span className="summary-value">
              {Number(price) === 0 ? (lng === 'en' ? 'Free' : 'Gratuito') : `${price} ${currency}`}
            </span>
          </div>
        )}
      </div>

      {booking.requiresPayment && (
        <div className="payment-notice">
          <p>{tl('booking.requires_payment')}</p>
        </div>
      )}

      <div className="confirmation-question">
        <p>{tl('booking.confirm_question')}</p>
      </div>

      <div className="booking-actions">
        <button type="button" className="btn-secondary btn-cancel" onClick={onCancel}>
          {tl('booking.cancel')}
        </button>
        <button type="button" className="btn-primary btn-confirm" onClick={onConfirm}>
          {confirmLabel ?? tl('booking.confirm_booking')}
        </button>
      </div>
    </div>
  );
}
